import { createRoot } from 'solid-js'
import { createStore } from 'solid-js/store'
import { Loading } from '~/lib/loading'
import { invalidateBalance } from '~/resources/currency'
import type { GrantedContentType } from '~/resources/claim-reward'
import { flying } from '~/store/reward'

export interface ItemBase {
  title: string
  icon: string
  label?: string
  bgAnimType: 1 | 2 | 3
  resultBgColor: string
  resultTextGradient?: string
  type: GrantedContentType
  value: number
  isBigPrize: boolean
}

export type Item = ItemBase

type Source = { x: number; y: number }

export type PreviewAnimState = {
  variant: 'chestbox_open'
  chapter: 1 | 2 | 3
}

type State = {
  items: Item[] | null
  currentIndex: number
  source: Source | null
  previewAnim: PreviewAnimState | null
  // CSS background overriding the flow's default backdrop for this run —
  // the special challenge themes the reveal per event (reward_reveal_bg).
  background: string | null
}

type StartInput = {
  items: Item[]
  source?: Source
  previewAnim?: PreviewAnimState | null
  background?: string
}

const FLYERS_PER_CURRENCY = 3
const BALANCE_FLUSH_DELAY_MS = 600

function spawnFlyersForItems(items: Item[], source: Source) {
  const totalCoin = items
    .filter((i) => i.type === 'coin')
    .reduce((s, i) => s + i.value, 0)
  const totalGem = items
    .filter((i) => i.type === 'gem')
    .reduce((s, i) => s + i.value, 0)

  if (totalCoin > 0) spawn('coin', source, '[data-coin-target]')
  if (totalGem > 0) spawn('gem', source, '[data-gem-target]')
}

function spawn(kind: 'coin' | 'gem', source: Source, targetSelector: string) {
  const target = document.querySelector<HTMLElement>(targetSelector)
  if (!target) return
  const t = target.getBoundingClientRect()
  flying.spawn(
    Array.from({ length: FLYERS_PER_CURRENCY }, (_, i) => ({
      kind,
      startX: source.x + (i - 1) * 6,
      startY: source.y + (i % 2 === 0 ? -4 : 4),
      endX: t.left + t.width / 2,
      endY: t.top + t.height / 2,
    })),
  )
}

export const { rewardFlow, rewardState } = createRoot(() => {
  const [state, setState] = createStore<State>({
    items: null,
    currentIndex: 0,
    source: null,
    previewAnim: null,
    background: null,
  })
  let complete: Loading<void> | null = null

  const rewardFlow = {
    start(input: StartInput): Promise<void> {
      setState({
        items: input.items,
        currentIndex: 0,
        source: input.source ?? null,
        previewAnim: input.previewAnim ?? null,
        background: input.background ?? null,
      })
      complete = new Loading<void>()
      return complete.promise
    },
    next() {
      const items = state.items
      if (!items) return
      if (state.currentIndex === items.length - 1) {
        const src = state.source
        if (src) spawnFlyersForItems(items, src)
        setState({
          items: null,
          source: null,
          previewAnim: null,
          background: null,
        })
        window.setTimeout(invalidateBalance, BALANCE_FLUSH_DELAY_MS)
        complete?.done()
        complete = null
        return
      }
      setState('currentIndex', state.currentIndex + 1)
    },
    endPreviewAnim() {
      setState('previewAnim', null)
    },
  }

  return { rewardFlow, rewardState: state }
})
